import { AnimatePresence, motion } from 'framer-motion';

interface ContactJumpCueProps {
  /**
   * Rendered only while true; `AnimatePresence` unmounts it once the exit
   * animation finishes, so it does not linger as a tab stop.
   *
   * In practice this is a pointer-only affordance: it is last in DOM order and
   * focusing anything in `#contact` brings that section into view, which hides
   * the cue — so sequential Tab never lands on it. That is intentional, and it
   * costs nothing: the header `Contact` link is the equivalent keyboard path.
   * Do not move it earlier in the DOM to "fix" this — a floating action button
   * as the first tab stop inside `<main>` is strictly worse.
   */
  isVisible: boolean;
  to: string;
  label: string;
  ariaLabel?: string;
}

const surfaceClass =
  'jump-cue nav-surface fixed z-40 inline-flex min-h-[44px] items-center gap-2.5 rounded-full border border-[color:var(--hairline)] px-4 shadow-panel backdrop-blur-md';

// No `focus-visible:outline-none`: a class-level rule would beat the global
// `a:focus-visible` outline in index.css and leave no visible focus indicator.
const textClass =
  'font-mono text-xs font-semibold uppercase tracking-[0.16em] text-[color:var(--fg)] transition-colors hover:border-primary-dark hover:text-primary-ink dark:hover:border-primary dark:hover:text-primary';

/**
 * Floating jump-to-contact cue: a detached piece of the navbar docked
 * bottom-right, carrying the hero's live signal dot so it reads as an open line
 * rather than a support widget.
 */
const ContactJumpCue = ({ isVisible, to, label, ariaLabel }: ContactJumpCueProps): JSX.Element => (
  <AnimatePresence>
    {isVisible && (
      <motion.a
        href={to}
        aria-label={ariaLabel}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 8 }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
        className={`${surfaceClass} ${textClass}`}
      >
        <span className="relative flex h-2 w-2 shrink-0" aria-hidden="true">
          <span className="absolute inline-flex h-full w-full rounded-full bg-primary-dark animate-pulse-signal dark:bg-primary" />
        </span>
        {label}
        <span aria-hidden="true" className="text-[color:var(--fg-muted)]">
          ↓
        </span>
      </motion.a>
    )}
  </AnimatePresence>
);

export default ContactJumpCue;
